<template>
  <!-- Rendered at shell level so it exists on EVERY authenticated page and its
       state (transcript + open/closed) survives route changes. -->
  <template v-if="canUseAssistant && !onAssistantPage">
    <!-- Launcher -->
    <button
      v-show="!open"
      type="button"
      class="fixed z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
      :class="launcherPosition"
      aria-label="Open AI Assistant"
      data-testid="ai-launcher"
      @click="openPanel"
    >
      <i class="pi text-xl" :class="chat.isStreaming.value ? 'pi-spin pi-spinner' : 'pi-sparkles'"></i>
    </button>

    <!--
      Floating chat panel. A NON-modal card that pops up from where the
      launcher sits (same corner, same offset), so the page behind it stays
      visible and usable — you can chat while reading the screen you asked
      about. Below md it becomes a full-screen sheet (max-md:inset-0 wins over
      the anchor classes because variant utilities are emitted after them).
    -->
    <Transition name="ai-float">
      <section
        v-if="open"
        class="fixed z-50 flex flex-col bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden
          w-[26rem] h-[40rem] max-h-[calc(100vh-8rem)]
          max-md:inset-0 max-md:w-auto max-md:h-auto max-md:max-h-none max-md:rounded-none max-md:border-0"
        :class="launcherPosition"
        role="dialog"
        aria-label="AI Assistant"
        data-testid="ai-float-panel"
      >
        <!-- Header: identity, active chat, history + new chat, expand, close -->
        <header class="flex items-center gap-2 px-4 py-3 border-b border-gray-100 shrink-0">
          <i class="pi pi-sparkles text-primary text-lg shrink-0"></i>
          <div class="flex flex-col min-w-0 flex-1">
            <span class="font-semibold text-gray-800 leading-tight">AI Assistant</span>
            <span v-if="chat.conversationTitle.value" class="text-xs text-gray-400 truncate"
              :title="chat.conversationTitle.value" data-testid="ai-active-title">
              {{ chat.conversationTitle.value }}
            </span>
          </div>

          <AiChatHistoryMenu
            :list="conversationList"
            :activeId="chat.conversationId.value"
            @select="onSelectConversation"
            @new="chat.startNewChat()"
            @refresh="conversationList.refresh()"
            @deleted="onDeleted"
          />

          <!-- Same control style as AiChatHistoryMenu so the header reads as one row -->
          <button type="button"
            class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-primary transition-colors shrink-0"
            aria-label="Open full page" title="Open full page"
            data-testid="ai-panel-expand"
            @click="expandToPage">
            <i class="pi pi-window-maximize text-sm"></i>
          </button>
          <button type="button"
            class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-primary transition-colors shrink-0"
            aria-label="Close" title="Close"
            data-testid="ai-panel-close"
            @click="open = false">
            <i class="pi pi-times text-sm"></i>
          </button>
        </header>

        <!--
          View switch. The full page shows the workspace BESIDE the chat; a
          26rem card cannot, so the same cards live behind a second tab. The
          count badge and dot say "the model put something here" without
          yanking you off the answer you are reading.
        -->
        <nav class="flex items-center gap-1 px-4 py-2 border-b border-gray-100 shrink-0" role="tablist"
          aria-label="Assistant views">
          <button type="button" role="tab" :aria-selected="view === 'chat'"
            class="px-3 py-1 text-xs font-medium rounded-full transition-colors"
            :class="view === 'chat' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'"
            data-testid="ai-view-chat"
            @click="showView('chat')">
            Chat
          </button>
          <button type="button" role="tab" :aria-selected="view === 'workspace'"
            class="relative inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-colors"
            :class="view === 'workspace' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100'"
            data-testid="ai-view-workspace"
            @click="showView('workspace')">
            Workspace
            <span v-if="chat.workspaceCards.value.length"
              class="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-[11px] leading-none"
              :class="view === 'workspace' ? 'bg-white/25 text-white' : 'bg-primary/10 text-primary'"
              data-testid="ai-workspace-count">
              {{ chat.workspaceCards.value.length }}
            </span>
            <span v-if="unseenCards" class="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary ring-2 ring-white"
              data-testid="ai-workspace-new-dot" aria-hidden="true"></span>
          </button>
        </nav>

        <!-- Chat view -->
        <div v-if="view === 'chat'" class="flex flex-col flex-1 min-h-0 p-4" data-testid="ai-view-chat-body">
          <!-- A card landed while you were reading — offer the jump, don't force it. -->
          <button v-if="unseenCards" type="button"
            class="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border border-primary/20 bg-primary/5
              text-xs text-primary text-left hover:bg-primary/10 transition-colors shrink-0"
            data-testid="ai-new-cards-banner"
            @click="showView('workspace')">
            <i class="pi pi-objects-column"></i>
            <span class="flex-1">{{ unseenCards === 1 ? '1 new card' : `${unseenCards} new cards` }} in the workspace</span>
            <span class="font-medium">View</span>
          </button>
          <AiAssistantChatPanel :chat="chat" :isStudent="isStudent">
            <template #above-composer>
              <!-- Accept / decline proposed changes right here; Details opens the full card. -->
              <div v-if="decisions.length" class="space-y-1.5 mt-2" data-testid="ai-inline-decisions">
                <AiInlineDecision v-for="d in decisions" :key="d.cardId"
                  :pending-action-id="d.pendingActionId"
                  @details="showView('workspace')" />
              </div>
            </template>
          </AiAssistantChatPanel>
        </div>

        <!-- Workspace view: the very same cards the full page shows beside the chat -->
        <div v-else class="flex flex-col flex-1 min-h-0" data-testid="ai-view-workspace-body">
          <AiWorkspace
            flush
            :cards="chat.workspaceCards.value"
            :mode="chat.workspaceMode.value"
            @update:mode="chat.setWorkspaceMode"
            @dismiss="chat.dismissWorkspaceCard"
            @clear="chat.clearWorkspaceCards"
            @action="onCardAction"
          />
        </div>
      </section>
    </Transition>

    <!--
      Card quick-actions that open a drawer/modal layer OVER the panel (PrimeVue
      overlays sit above z-50), so the transcript and cards stay mounted behind
      them and closing returns you to the panel.
    -->
    <AiActionSurface :surface="actionSurface" @close="actionSurface = null" />
  </template>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import AiAssistantChatPanel from '@/components/common/AiAssistantChatPanel.vue';
import AiChatHistoryMenu from '@/components/common/AiChatHistoryMenu.vue';
import AiWorkspace from '@/components/common/AiWorkspace.vue';
import AiActionSurface from '@/components/common/ai-cards/AiActionSurface.vue';
import AiInlineDecision from '@/components/common/ai-cards/AiInlineDecision.vue';
import type { AiActionOutcome } from '@/components/common/ai-cards/aiCardActions';
import { useAiChat } from '@/composables/useAiChat';
import { useAiConversations } from '@/composables/useAiConversations';
import { useAiAssistantAccess } from '@/composables/useAiAssistantAccess';
import { inlineDecisions } from '@/utils/aiInlineDecisions';

/**
 * Global floating AI assistant: launcher button + floating chat panel.
 * Mounted ONCE per dashboard shell (admin + student), outside <router-view>,
 * so it is reachable from every page. The chat state lives HERE (not inside
 * the panel content, which unmounts on close), so the transcript survives
 * both closing the panel and navigating between pages.
 *
 * The panel is a floating card (not a modal drawer): it takes the launcher's
 * corner and leaves the rest of the page interactive. It has two views — the
 * chat, and the workspace cards the model chose to show (the same cards the
 * full page renders beside its chat) — plus an "expand" action to the full
 * page when you want both side by side.
 *
 * Browse/resume: the header carries a "recent chats" menu (AiConversationList)
 * and a New chat action. Opening the panel restores the conversation this
 * user+school last used, so a reload resumes rather than starting over.
 */
withDefaults(
  defineProps<{
    /**
     * Tailwind position classes for the launcher AND the panel's anchor —
     * lets the student shell dodge TicketingWidget/MobileBottomNav.
     */
    launcherPosition?: string;
  }>(),
  { launcherPosition: 'bottom-6 right-6' },
);

const { canUseAssistant, isStudent } = useAiAssistantAccess();

// The full page IS the assistant, so a launcher for it there is noise —
// and opening the panel on top of it would give you two live chats.
const route = useRoute();
const router = useRouter();
const onAssistantPage = computed(
  () => route?.name === 'admin-ai-assistant' || route?.name === 'student-ai-assistant',
);

const conversationList = useAiConversations();

// Shell-level chat state: one transcript per dashboard shell, shared by every
// page. A new/adopted conversation refreshes the browse list so the id the
// server just minted shows up without a manual reload.
const chat = useAiChat({
  onConversationChange: (id) => {
    if (id !== null) conversationList.refresh();
  },
});

const open = ref(false);

/** Proposed changes repeated inline in the chat view, so accepting them doesn't need the Workspace tab. */
const decisions = computed(() => inlineDecisions(chat.workspaceCards.value));

/** Which of the two panel views is showing. Sticky across close/reopen. */
const view = ref<'chat' | 'workspace'>('chat');

/**
 * Cards that arrived while the workspace view was NOT showing. Drives the dot
 * on the tab and the "new cards" banner in the chat view; viewing the
 * workspace clears it. Tracked by id so a stack/replace/dismiss churn never
 * double-counts the same card.
 */
const unseenCards = ref(0);
let knownCardIds = new Set(chat.workspaceCards.value.map((c) => c.id));
watch(
  () => chat.workspaceCards.value,
  (cards) => {
    const ids = new Set(cards.map((c) => c.id));
    if (view.value !== 'workspace') {
      unseenCards.value += cards.filter((c) => !knownCardIds.has(c.id)).length;
    }
    knownCardIds = ids;
  },
  { deep: true },
);

const showView = (next: 'chat' | 'workspace') => {
  view.value = next;
  if (next === 'workspace') unseenCards.value = 0;
};

const openPanel = async() => {
  open.value = true;
  // Resume where this user left off (no-op once a transcript is in memory).
  await chat.resumeStoredConversation();
  if (!conversationList.loaded.value) conversationList.refresh();
};

// Esc closes the floating panel, like the modal drawer it replaced did. The
// transcript stays in memory, so reopening picks up exactly where you were.
useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && open.value) open.value = false;
});

// Hand over to the full-page assistant (workspace + chat). Both hosts read
// the same stored conversation id, so the page resumes this very chat.
const expandToPage = () => {
  open.value = false;
  router?.push({ name: isStudent.value ? 'student-ai-assistant' : 'admin-ai-assistant' });
};

/** The drawer/modal currently layered over the panel, if any. */
const actionSurface = ref<Extract<AiActionOutcome, { mode: 'drawer' | 'modal' }> | null>(null);

/**
 * Carry out a card's quick action in its declared containment mode — the same
 * contract as the full page. Navigate closes the panel (you asked to go look
 * at something; the launcher brings the chat straight back), embed adds a card
 * beside the others, drawers/modals open over the panel.
 */
const onCardAction = (outcome: AiActionOutcome) => {
  if (outcome.mode === 'navigate') {
    open.value = false;
    router?.push(outcome.route);
    return;
  }
  if (outcome.mode === 'embed') {
    chat.addWorkspaceCard(outcome.card);
    return;
  }
  actionSurface.value = outcome;
};

const onSelectConversation = async(id: number) => {
  // loadConversation aborts any in-flight stream before swapping transcripts.
  await chat.loadConversation(id);
};

const onDeleted = (id: number) => {
  // Deleting the open chat leaves the transcript orphaned — start clean.
  if (chat.conversationId.value === id) chat.startNewChat();
};

// Restore on mount too: a page reload with the panel closed should still have
// the conversation ready the moment it opens.
onMounted(() => {
  if (canUseAssistant.value) chat.resumeStoredConversation();
});
</script>

<style scoped>
/* Pop up from the launcher's corner rather than sliding in from the edge. */
.ai-float-enter-active,
.ai-float-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
  transform-origin: bottom right;
}
.ai-float-enter-from,
.ai-float-leave-to {
  opacity: 0;
  transform: translateY(0.75rem) scale(0.96);
}
</style>
